import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useRouterCore } from "../../../cores/router";
import {
  platformAutomationsApi,
  PlatformAutomation,
} from "../../../cores/platformAutomations/services/api";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import {
  EmailTemplateLibrary,
  emailTemplateLibraryFilters,
} from "../emailMarketing/EmailTemplateLibrary";
import "../emailMarketing/EmailMarketing.scss";

const STATUS_ACCENT: Record<string, string> = {
  published: "#2563eb",
  draft: "#f59e0b",
  paused: "#94a3b8",
  archived: "#64748b",
};

const countEmailSteps = (automation: PlatformAutomation) => {
  const steps = automation.draftDefinition?.steps;
  if (!Array.isArray(steps)) return 0;
  return steps.filter((step) => step?.type === "send_email").length;
};

export default function PlatformAutomationsList() {
  const intl = useIntl();
  const { goToPlatformAutomationEdit, goToPlatformMessageTemplates } =
    useRouterCore();
  const [items, setItems] = useState<PlatformAutomation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "published" | "draft" | "paused">(
    "all"
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await platformAutomationsApi.list());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statusLabel = (status: string) =>
    intl.formatMessage({
      id: `app.platformAutomations.status.${status}`,
      defaultMessage: status,
    });

  const libraryItems = useMemo(
    () =>
      (filter === "all"
        ? items
        : items.filter((item) => item.status === filter)
      ).map((item) => {
        const active = item.stats?.active ?? 0;
        const steps = countEmailSteps(item);
        const dateLabel = item.updatedAt
          ? new Date(item.updatedAt).toLocaleDateString("es-CL")
          : null;
        const metaParts = [
          statusLabel(item.status),
          intl.formatMessage(
            { id: "app.platformAutomations.flows.metaActive" },
            { count: active }
          ),
          intl.formatMessage(
            { id: "app.platformAutomations.flows.metaSteps" },
            { count: steps }
          ),
        ];
        if (dateLabel) metaParts.push(dateLabel);

        return {
          id: String(item.id),
          name: item.name,
          updatedAt: metaParts.join(" · "),
          accent: STATUS_ACCENT[item.status] || "#64748b",
          filterType: item.status,
        };
      }),
    [filter, items, intl]
  );

  const onCreate = async () => {
    const automation = await platformAutomationsApi.create({
      name: intl.formatMessage({ id: "app.platformAutomations.flows.newName" }),
      draftDefinition: {
        trigger: "user_email_verified",
        enrollmentPolicy: "once_per_user",
        defaultLocale: "es",
        defaultTimezone: "America/Santiago",
        conditions: {
          all: [{ field: "freeTrial", operator: "eq", value: true }],
        },
        stopRules: [
          { event: "converted", stopScope: "automation" },
          { event: "unsubscribed", stopScope: "channel", channel: "email" },
        ],
        steps: [],
      },
    });
    goToPlatformAutomationEdit(automation.id);
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--templates">
        {loading ? (
          <p className="email-mkt__note">
            <FormattedMessage id="app.platformAutomations.flows.loading" />
          </p>
        ) : (
          <EmailTemplateLibrary
            eyebrow={
              <FormattedMessage id="app.platformAutomations.templatesLibrary.eyebrow" />
            }
            title={
              <FormattedMessage id="app.platformAutomations.title" />
            }
            subtitle={
              <FormattedMessage id="app.platformAutomations.subtitle" />
            }
            statsLabel={
              <FormattedMessage id="app.platformAutomations.flows.stats" />
            }
            statsCount={items.length}
            templates={libraryItems}
            filters={emailTemplateLibraryFilters.automations}
            activeFilter={filter}
            onFilterChange={(next) =>
              setFilter(next as "all" | "published" | "draft" | "paused")
            }
            createLabel={
              <FormattedMessage id="app.platformAutomations.flows.create" />
            }
            onCreate={onCreate}
            actionLabel={
              <FormattedMessage id="app.platformAutomations.flows.edit" />
            }
            onTemplateAction={(flowId) =>
              goToPlatformAutomationEdit(Number(flowId))
            }
            emptyLabel={
              <FormattedMessage id="app.platformAutomations.flows.empty" />
            }
            toolbarExtra={
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={goToPlatformMessageTemplates}
              >
                <FormattedMessage id="app.platformAutomations.templates" />
              </button>
            }
          />
        )}
      </Content>
    </Layout>
  );
}
